import { useEffect, useState } from "react";
import { Mail, Phone, Loader2, Instagram, Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { trackContactClick, trackContactCopy } from "@/lib/tracker";

const BOX = "inline-flex items-center gap-2 px-3 py-1.5 rounded-md border hairline text-sm";
const handleOf = (url) => {
  try { return "@" + new URL(url).pathname.split("/").filter(Boolean)[0]; } catch { return url; }
};

// v62 — Instagram, only inside "View contact". Opening / copying is tracked (never the handle).
function InstagramContact({ channelId, url, unsure, testid, section }) {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      trackContactCopy(channelId, "instagram", section);
      toast.success("Instagram copied");
    } catch { toast.error("Copy failed"); }
  };
  return (
    <span className="inline-flex flex-col gap-1" data-testid={testid}>
      <span className="inline-flex items-center gap-2">
        <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-link`} data-contact-kind="instagram"
          onClick={() => trackContactClick(channelId, "instagram")}
          className={`${BOX} hover:border-white/30 transition-colors duration-150 group`}>
          <Instagram size={14} className="text-pink-400" />
          <span className="mono text-xs text-zinc-300 group-hover:text-white truncate max-w-[200px]">{handleOf(url)}</span>
          <ExternalLink size={11} className="text-zinc-500" />
        </a>
        <button type="button" onClick={copy} data-testid={`${testid}-copy`} title="Copy the Instagram link"
          className="inline-flex items-center justify-center w-8 h-8 rounded-md border hairline text-zinc-400 hover:text-white hover:border-white/30 transition-colors duration-150">
          <Copy size={12} />
        </button>
      </span>
      <span className="text-[11px] text-zinc-500 leading-tight" data-testid={`${testid}-note`}>
        {unsure && <span className="text-amber-400/80" data-testid={`${testid}-unsure`}>Unsure — admin only · </span>}
        Best guess — please verify before reaching out.
      </span>
    </span>
  );
}

// v51 — "View contact" looks the same on every creator. The details (or
// "not available") are fetched, and the press recorded, only on click.
export function ViewContact({ endpoint, testid, render, channelId, place = null }) {
  const [contact, setContact] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setContact(null); }, [endpoint]);
  if (contact) {
    const ig = contact.contact_instagram && (
      <InstagramContact channelId={channelId} url={contact.contact_instagram} unsure={!!contact.instagram_unsure} testid={`${testid}-instagram`} section={place ? "c360_profile" : undefined} />
    );
    if (contact.contact_email || contact.contact_phone) return <span className="db-t">{render(contact)}{ig}</span>;
    if (ig) return <span className="db-t">{ig}</span>;
    return <span data-testid={`${testid}-none`} className={`${BOX} text-zinc-500`}><span className="mono text-xs">Contact details not available</span></span>;
  }
  const open = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(endpoint, null, place ? { params: { place } } : undefined);
      setContact(data || {});
    } catch { /* leave the link in place */ }
    finally { setBusy(false); }
  };
  return (
    <button type="button" data-testid={testid} onClick={open}
      className={`${BOX} hover:border-white/30 transition-colors duration-150 group`}>
      {busy ? <Loader2 size={14} className="animate-spin text-zinc-400" /> : (
        <><Mail size={14} className="text-fuchsia-400" /><Phone size={14} className="text-amber-400" /></>
      )}
      <span className="mono text-xs text-zinc-300 group-hover:text-white">View contact</span>
    </button>
  );
}
